import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, 
  CheckCircle2, 
  Clock, 
  Target, 
  TrendingUp, 
  ShieldCheck, 
  Layers, 
  Zap, 
  Hospital, 
  Building2, 
  ArrowDown, 
  Calendar, 
  Factory, 
  ShoppingBag, 
  Briefcase, 
  Scan,
  ShieldAlert,
  PhoneCall,
  Activity,
  Cpu,
  MousePointer2,
  Database,
  PieChart,
  ChevronDown,
  Network,
  Globe,
  MessageSquareWarning,
  AlertCircle,
  ClipboardCheck,
  Plus,
  Minus
} from 'lucide-react';

interface PilotPageProps {
  onNavigate: (view: 'home' | 'pilot' | 'privacy' | 'terms') => void;
}

const StrategicFitSection = () => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({
    focused: false,
    multi: false,
    complex: false
  });

  const toggleAccordion = (id: string) => {
    setOpenStates(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const categories = [
    {
      id: 'focused',
      icon: <Zap className="w-6 h-6" />,
      title: "Focused Operations",
      subtitle: "Time-Critical Decisions",
      hook: "Small disruptions escalate quickly when decisions are delayed. You need early signals, not post-mortems.",
      useCases: [
        "Late supplier confirmation forcing last-minute expediting",
        "Missed early warning on material shortage before order cutoff",
        "Delayed recognition of demand spike causing service failure",
        "Quality issue escalation discovered after production commitment",
        "Capacity constraint identified after schedules are locked",
        "Inventory shortfall noticed only at fulfilment stage",
        "Maintenance risk flagged too late to avoid downtime"
      ]
    },
    {
      id: 'multi',
      icon: <Network className="w-6 h-6" />,
      title: "Multi-Node Operations",
      subtitle: "Team & Location Alignment",
      hook: "Decisions span teams, sites, or partners. Alignment depends on shared understanding, not delayed reports.",
      useCases: [
        "Conflicting views between operations and finance on urgency",
        "Site-level issues escalated too late to central teams",
        "Local workarounds hidden in emails or spreadsheets",
        "Slow cross-team agreement during exceptions",
        "Delays caused by reconciling “whose numbers are right”",
        "Action blocked while teams wait for updated reports",
        "Repeated handoffs causing decision paralysis"
      ]
    },
    {
      id: 'complex',
      icon: <Globe className="w-6 h-6" />,
      title: "Distributed Operations",
      subtitle: "High Complexity Scaling",
      hook: "Scale and interdependencies increase risk. Confidence matters as much as speed.",
      useCases: [
        "Early disruption signals lost in operational noise",
        "Multiple dependencies masking root cause of risk",
        "Decisions delayed due to fear of unintended downstream impact",
        "Scenarios discussed too late, under pressure",
        "Escalations triggered only after thresholds are breached",
        "Leadership forced into reactive decisions without context"
      ]
    }
  ];

  return (
    <div className="mb-32">
      <div className="text-center mb-20 max-w-3xl mx-auto">
        <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent mb-6">DIAGNOSTIC ALIGNMENT</p>
        <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-zodiac mb-8">Is this Pilot right for you?</h2>
        <p className="text-lg text-zodiac/50 font-light leading-relaxed">
          The Groflex Pilot is a deterministic engagement for mid-markets organizations or enterprise where timing is the primary differentiator of success.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {categories.map((cat) => {
          const isOpen = openStates[cat.id];
          return (
            <div key={cat.id} className="group relative bg-white border border-zodiac/5 rounded-[3rem] p-10 shadow-sm hover:shadow-2xl transition-all duration-700 flex flex-col h-full overflow-hidden">
              <div className="w-14 h-14 rounded-2xl bg-brandBg flex items-center justify-center mb-8 text-zodiac group-hover:bg-accent group-hover:text-white transition-all duration-500 shadow-inner shrink-0">
                {cat.icon}
              </div>
              
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-zodiac tracking-tight mb-2">{cat.title}</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-accent mb-6">{cat.subtitle}</p>
                <p className="text-sm text-zodiac/60 font-medium leading-relaxed italic border-l-2 border-accent/20 pl-4">
                  "{cat.hook}"
                </p>
              </div>

              <div className="flex-grow">
                <button 
                  onClick={() => toggleAccordion(cat.id)}
                  className="w-full flex items-center justify-between py-4 border-t border-zodiac/5 group/btn"
                >
                  <p className="text-[9px] font-black uppercase tracking-widest text-zodiac/40 group-hover/btn:text-accent transition-colors">Common Use Cases</p>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-accent text-white' : 'bg-zodiac/5 text-zodiac/30'}`}>
                    {isOpen ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  </div>
                </button>

                <div className={`space-y-4 overflow-hidden transition-all duration-700 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100 mt-4 pb-4' : 'max-h-0 opacity-0'}`}>
                  {cat.useCases.map((useCase, idx) => (
                    <div key={idx} className="flex gap-3 items-start group/item">
                      <div className="mt-1 w-1.5 h-1.5 rounded-full bg-accent/20 group-hover/item:bg-accent transition-colors shrink-0"></div>
                      <p className="text-[13px] text-zodiac/70 font-medium leading-tight group-hover/item:text-zodiac transition-colors">
                        {useCase}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ROICalculator = () => {
  const [industry, setIndustry] = useState<'manufacturing' | 'healthcare' | 'privateEquity' | 'retail' | 'generalOperations'>('manufacturing');
  const [revenue, setRevenue] = useState(250); 
  const [driftRate, setDriftRate] = useState(1.5); 
  const [isCalculating, setIsCalculating] = useState(false);
  const [count, setCount] = useState(0);
  const BOOKING_URL = "https://outlook.office.com/bookwithme/user/00ab44e829054d0caf6074d9ffaa2153@groflex.ai/meetingtype/0S1TPXwvgk2Nip5dsFYVgQ2?anonymous&ismsaljsauthenabled&ep=mcard";

  const industryConfig = {
    manufacturing: { driftDefault: 1.4, recovery: '92 days', riskScore: 88, label: 'Manufacturing', icon: <Factory className="w-5 h-5" /> },
    healthcare: { driftDefault: 3.8, recovery: '65 days', riskScore: 94, label: 'Healthcare', icon: <Hospital className="w-5 h-5" /> },
    privateEquity: { driftDefault: 1.8, recovery: '120 days', riskScore: 78, label: 'Private Equity', icon: <PieChart className="w-5 h-5" /> },
    retail: { driftDefault: 2.4, recovery: '110 days', riskScore: 82, label: 'Retail', icon: <ShoppingBag className="w-5 h-5" /> },
    generalOperations: { driftDefault: 2.0, recovery: '105 days', riskScore: 85, label: 'General Operations', icon: <Briefcase className="w-5 h-5" /> }
  };

  const finalValue = Number((revenue * (driftRate / 100) * 0.35).toFixed(2));

  useEffect(() => {
    setDriftRate(industryConfig[industry].driftDefault);
  }, [industry]);

  useEffect(() => {
    setIsCalculating(true);
    const timer = setTimeout(() => setIsCalculating(false), 800);
    
    // Simple animated counter effect
    let start = 0;
    const end = finalValue;
    const duration = 500;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = start + (end - start) * progress;
      setCount(current);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    return () => clearTimeout(timer);
  }, [revenue, driftRate, industry, finalValue]);

  const handleSliderChange = (setter: React.Dispatch<React.SetStateAction<number>>, val: number) => {
    setter(val);
  };

  return (
    <div className="relative group max-w-6xl mx-auto">
      {/* Dynamic Background Ambiance */}
      <div className="absolute -inset-10 bg-accent/10 blur-[120px] rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-1000"></div>
      
      <div className="relative bg-[#0B1222] border border-white/10 rounded-[4rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.6)]">
        {/* Technical Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" 
             style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}>
        </div>

        <div className="grid lg:grid-cols-12 min-h-[700px]">
          {/* CONTROL PANEL */}
          <div className="lg:col-span-7 p-8 md:p-12 lg:p-16 border-r border-white/5 relative bg-gradient-to-br from-white/[0.02] to-transparent">
            <div className="flex items-center justify-between mb-12">
              <div className="inline-flex items-center space-x-3">
                <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center border border-accent/20">
                  <Activity className="w-4 h-4 text-accent" />
                </div>
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-accent">Strategic Impact Engine v4.0</p>
              </div>
              <div className="flex items-center gap-2">
                 <div className={`w-1.5 h-1.5 rounded-full ${isCalculating ? 'bg-accent animate-pulse shadow-[0_0_10px_#00A1E4]' : 'bg-green-500 shadow-[0_0_10px_#22c55e]'}`}></div>
                 <span className="text-[9px] font-black uppercase tracking-widest text-white/20">{isCalculating ? 'Computing...' : 'Calibrated'}</span>
              </div>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-14 tracking-tight leading-tight max-w-lg">
              Quantify your <span className="text-accent italic">operational leakage</span> and recovery path.
            </h2>

            {/* INDUSTRY DROPDOWN SELECTOR */}
            <div className="relative mb-16">
              <label className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30 block mb-4">Select Operational Domain</label>
              <div className="relative group/dropdown">
                <div className="absolute left-6 top-1/2 -translate-y-1/2 text-accent/60 pointer-events-none">
                  {industryConfig[industry].icon}
                </div>
                <select 
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value as any)}
                  className="w-full bg-white/5 border border-white/10 text-white pl-16 pr-12 py-6 rounded-2xl appearance-none focus:outline-none focus:border-accent transition-all cursor-pointer font-bold tracking-tight text-lg shadow-inner"
                >
                  {Object.entries(industryConfig).map(([key, config]) => (
                    <option key={key} value={key} className="bg-[#0B1222] text-white">
                      {config.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none text-white/20 group-hover/dropdown:text-accent transition-colors">
                  <ChevronDown className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* PARAMETER SLIDERS */}
            <div className="space-y-16">
              <div className="group/slider relative">
                <div className="flex justify-between items-end mb-6">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30 block mb-2">Ops Revenue Base</label>
                    <div className="flex items-center gap-2">
                       <Database className="w-3.5 h-3.5 text-accent/40" />
                       <p className="text-sm font-medium text-white/50">Scale of connected decision loops.</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-5xl font-black text-white tracking-tighter tabular-nums">${revenue}M</span>
                  </div>
                </div>
                <div className="relative h-1.5 w-full bg-white/5 rounded-full mb-2">
                  <div className="absolute inset-y-0 left-0 bg-accent rounded-full shadow-[0_0_15px_rgba(0,161,228,0.4)] transition-all duration-300" style={{ width: `${(revenue / 2000) * 100}%` }}></div>
                  <input 
                    type="range" min="50" max="2000" step="50" value={revenue} 
                    onChange={(e) => handleSliderChange(setRevenue, Number(e.target.value))} 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                  />
                </div>
                <div className="flex justify-between px-1">
                   {[50, 500, 1000, 1500, 2000].map(val => (
                     <div key={val} className="flex flex-col items-center">
                        <div className={`w-0.5 h-1.5 bg-white/10 mb-1 ${revenue >= val ? 'bg-accent/40' : ''}`}></div>
                        <span className="text-[8px] font-black text-white/10">${val}M</span>
                     </div>
                   ))}
                </div>
              </div>

              <div className="group/slider relative">
                <div className="flex justify-between items-end mb-6">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-[0.3em] text-white/30 block mb-2">Execution Drift Rate</label>
                    <div className="flex items-center gap-2">
                       <ShieldAlert className="w-3.5 h-3.5 text-accent/40" />
                       <p className="text-sm font-medium text-white/50">Current leakage from coordination lag.</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-5xl font-black text-white tracking-tighter tabular-nums">{driftRate}%</span>
                  </div>
                </div>
                <div className="relative h-1.5 w-full bg-white/5 rounded-full mb-2">
                  <div className="absolute inset-y-0 left-0 bg-accent rounded-full shadow-[0_0_15px_rgba(0,161,228,0.4)] transition-all duration-300" style={{ width: `${(driftRate / 10) * 100}%` }}></div>
                  <input 
                    type="range" min="0.5" max="10" step="0.1" value={driftRate} 
                    onChange={(e) => handleSliderChange(setDriftRate, Number(e.target.value))} 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" 
                  />
                </div>
                <div className="flex justify-between px-1">
                   {[1, 3, 5, 7, 9].map(val => (
                     <div key={val} className="flex flex-col items-center">
                        <div className={`w-0.5 h-1.5 bg-white/10 mb-1 ${driftRate >= val ? 'bg-accent/40' : ''}`}></div>
                        <span className="text-[8px] font-black text-white/10">{val}%</span>
                     </div>
                   ))}
                </div>
              </div>
            </div>
          </div>

          {/* DIAGNOSTIC OUTPUT */}
          <div className="lg:col-span-5 bg-white/[0.01] backdrop-blur-xl p-8 md:p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden">
             {/* Decorative Scan Lines */}
             <div className="absolute inset-0 pointer-events-none opacity-20 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(transparent_0%,rgba(0,161,228,0.1)_50%,transparent_100%)] animate-[marquee_8s_linear_infinite]"></div>
             </div>

             <div className="relative z-10">
                <div className="flex flex-col items-center text-center mb-16">
                  <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-8 border border-accent/20">
                    <TrendingUp className="w-8 h-8 text-accent" />
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-[0.5em] text-accent mb-6">Annual Recovery Target</p>
                  
                  <div className="relative inline-block">
                    <div className={`text-7xl md:text-8xl lg:text-9xl font-black text-white tracking-tighter tabular-nums transition-all duration-300 ${isCalculating ? 'opacity-20 blur-xl scale-95' : 'opacity-100 blur-0 scale-100'}`}>
                      ${count.toFixed(2)}M
                    </div>
                  </div>
                  
                  <div className="mt-8 flex flex-col items-center gap-4">
                     <p className="text-white/40 text-[11px] font-medium tracking-wide">Calculated on a 35% Execution Lift Index</p>
                     <div className="h-0.5 w-32 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-accent w-[35%] animate-pulse"></div>
                     </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 pt-12 border-t border-white/10">
                   <div className="p-6 bg-white/[0.03] border border-white/5 rounded-[2rem] group/card hover:bg-white/[0.06] transition-colors">
                      <div className="flex items-center gap-3 text-white/30 mb-4">
                        <span className="text-[9px] font-black uppercase tracking-widest text-accent/60">ROI Velocity</span>
                      </div>
                      <p className="text-2xl font-bold text-white tracking-tight">{industryConfig[industry].recovery}</p>
                      <p className="text-[9px] text-white/20 font-black uppercase tracking-widest mt-2">To Signal Parity</p>
                   </div>
                   <div className="p-6 bg-white/[0.03] border border-white/5 rounded-[2rem] group/card hover:bg-white/[0.06] transition-colors">
                      <div className="flex items-center gap-3 text-white/30 mb-4">
                        <span className="text-[9px] font-black uppercase tracking-widest text-accent/60">Decision Confidence</span>
                      </div>
                      <p className="text-2xl font-bold text-white tracking-tight">{industryConfig[industry].riskScore}%</p>
                      <p className="text-[9px] text-white/20 font-black uppercase tracking-widest mt-2">Governance Score</p>
                   </div>
                </div>

                <div className="mt-12 space-y-4">
                  <button 
                    onClick={() => window.open(BOOKING_URL, '_blank')} 
                    className="w-full py-6 bg-accent text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] hover:scale-[1.02] active:scale-[0.98] transition-all duration-500 shadow-[0_20px_40px_rgba(0,161,228,0.25)] flex items-center justify-center gap-4 group/cta"
                  >
                    Request Custom Audit
                    <MousePointer2 className="w-4 h-4 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1 transition-transform" />
                  </button>
                  <p className="text-[9px] text-center font-black uppercase tracking-widest text-white/10 italic">
                    Requires Integration Readiness Assessment
                  </p>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PilotRoadmap = () => {
  const phases = [
    { title: "Diagnostic Audit", weeks: "Weeks 1-2", desc: "Deep-dive mapping of one critical decision loop. Identify signal sources (ERP, Logs, Files) and execution friction points.", icon: <Target className="w-5 h-5" /> },
    { title: "System Overlay", weeks: "Weeks 3-5", desc: "Connection of signal center to the operational stack. Pilot-specific KPI mapping and initial signal validation.", icon: <Layers className="w-5 h-5" /> },
    { title: "Active Simulation", weeks: "Weeks 6-8", desc: "Live testing of agentic reasoning. Comparison of Groflex-detected signals vs. legacy manual escalations.", icon: <Zap className="w-5 h-5" /> },
    { title: "Value Validation", weeks: "Weeks 9-12", desc: "Final outcome assessment. Delivery of the Operational Health Audit and multi-year scale roadmap.", icon: <CheckCircle2 className="w-5 h-5" /> }
  ];
  return (
    <div className="relative pt-12">
      <div className="absolute top-[88px] left-[12.5%] right-[12.5%] h-px bg-zodiac/5 hidden lg:block overflow-hidden">
        <div className="h-full bg-gradient-to-r from-transparent via-accent/40 to-transparent w-full animate-[marquee_4s_linear_infinite] origin-left"></div>
      </div>
      <div className="grid lg:grid-cols-4 gap-12 relative z-10">
        {phases.map((phase, i) => (
          <div key={i} className="group relative">
            <div className="absolute -top-12 left-0 text-[10px] font-black text-zodiac/10 group-hover:text-accent/40 transition-colors">MILESTONE 0{i+1}</div>
            <div className="w-20 h-20 rounded-3xl bg-white border border-zodiac/5 shadow-sm flex items-center justify-center mb-8 group-hover:bg-accent group-hover:text-white group-hover:scale-110 transition-all duration-500 relative">
               <div className="absolute inset-0 rounded-3xl border border-accent/0 group-hover:border-accent/40 animate-ping opacity-0 group-hover:opacity-100"></div>
               {phase.icon}
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-accent mb-2">{phase.weeks}</p>
            <h4 className="text-xl font-bold text-zodiac mb-4 tracking-tight">{phase.title}</h4>
            <p className="text-sm text-zodiac/50 font-light leading-relaxed">{phase.desc}</p>
            {i < 3 && <div className="lg:hidden flex justify-center py-6 text-accent/20"><ArrowDown className="w-6 h-6 animate-bounce" /></div>}
          </div>
        ))}
      </div>
    </div>
  );
};

export const PilotPage: React.FC<PilotPageProps> = ({ onNavigate }) => {
  const BOOKING_URL = "https://outlook.office.com/bookwithme/user/00ab44e829054d0caf6074d9ffaa2153@groflex.ai/meetingtype/0S1TPXwvgk2Nip5dsFYVgQ2?anonymous&ismsaljsauthenabled&ep=mcard";

  return (
    <div className="pt-24 bg-brandBg min-h-screen text-zodiac font-sans selection:bg-accent selection:text-white">
      <section className="py-32 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center space-x-3 mb-8 py-2 px-5 rounded-full bg-zodiac text-white shadow-xl">
              <ShieldCheck className="w-4 h-4 text-accent" />
              <span className="text-[10px] uppercase tracking-[0.4em] font-black">Enterprise Pilot</span>
            </div>
            <h1 className="text-5xl md:text-8xl font-bold tracking-tighter leading-[0.95] mb-10">Governance for <span className="text-accent">Execution.</span></h1>
            <p className="text-xl md:text-2xl text-zodiac/60 font-light leading-relaxed mb-12 max-w-2xl">Groflex Enterprise Pilot is a high-touch, deterministic engagement designed for operations leaders who cannot afford failure. Validate our platform's impact on your most complex use case in 12 weeks.</p>
            <div className="flex flex-col sm:flex-row gap-8 items-center">
              <button onClick={() => window.open(BOOKING_URL, '_blank')} className="w-full sm:w-auto bg-zodiac text-white px-12 py-6 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl hover:bg-accent transition-executive flex items-center justify-center gap-4 group">
                <PhoneCall className="w-4 h-4" />
                Book A Strategic Call
              </button>
              <button onClick={() => window.open(BOOKING_URL, '_blank')} className="text-zodiac font-black text-xs uppercase tracking-[0.3em] hover:text-accent transition-executive border-b-2 border-zodiac/10 hover:border-accent pb-2">Request Pilot Pricing</button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative group animate-slide-up">
              <div className="absolute -inset-1 bg-gradient-to-r from-accent/10 to-zodiac/10 rounded-[2.5rem] blur-2xl opacity-0 group-hover:opacity-100 transition duration-700"></div>
              <div className="relative bg-white border border-zodiac/[0.03] rounded-[2.5rem] shadow-[0_40px_80px_-15px_rgba(17,34,72,0.08)] overflow-hidden">
                <div className="p-10 lg:p-14 space-y-12 bg-white relative">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <div className="w-2 h-2 rounded-full bg-accent shadow-[0_0_8px_rgba(0,161,228,0.6)]"></div>
                      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-accent">Availability</p>
                    </div>
                    <div className="bg-brandBg px-3 py-1 rounded text-[9px] font-bold text-zodiac/30 uppercase tracking-[0.15em]">Live Status</div>
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-4xl font-bold text-zodiac tracking-tight">2 Slots Remaining</h3>
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] text-zodiac/30">FOR THE PERIOD Q1 2026</p>
                  </div>
                  <div className="flex space-x-3 h-2">
                    {[1, 2, 3, 4, 5].map((i) => (<div key={i} className={`flex-grow rounded-full transition-all duration-1000 ${i >= 4 ? 'bg-accent shadow-[0_0_12px_rgba(0,161,228,0.5)]' : 'bg-zodiac/[0.08]'}`}></div>))}
                  </div>
                  <div className="bg-brandBg/60 p-8 rounded-[2rem] border border-zodiac/[0.03]">
                    <p className="text-sm font-bold text-zodiac mb-2 tracking-tight">Custom Price Validation Program</p>
                    <p className="text-[11px] text-zodiac/40 leading-relaxed font-medium">The pilot fee is credited toward your first year of enterprise scaling upon validation.</p>
                  </div>
                  <div className="space-y-6 pt-4">
                    <div className="flex items-center space-x-3">
                      <Calendar className="w-3.5 h-3.5 text-zodiac/20" /><p className="text-[10px] font-black uppercase tracking-[0.25em] text-zodiac/30">Duration</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <h3 className="text-4xl font-bold text-zodiac tracking-tight">12 Weeks</h3>
                      <div className="w-12 h-12 rounded-full bg-brandBg flex items-center justify-center border border-zodiac/[0.03]"><Clock className="w-5 h-5 text-zodiac/10" strokeWidth={1.5} /></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-40 bg-white border-y border-zodiac/5 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-24 items-start">
            <div className="space-y-12">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.4em] text-accent mb-6">PROOF IN PRACTICE</p>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zodiac mb-8">One of Japan's largest Medical Equipment Suppliers</h2>
                <p className="text-lg text-zodiac/50 font-light leading-relaxed">Solving critical resource allocation and inventory drift across a high-volume medical supply chain.</p>
              </div>
              <div className="p-10 bg-brandBg rounded-[3rem] border border-zodiac/5 grid sm:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-accent"><Building2 className="w-5 h-5" /><span className="text-[10px] font-black uppercase tracking-widest">Facility Profile</span></div>
                  <p className="text-2xl font-bold text-zodiac">~$650M+ Turnover</p>
                  <p className="text-sm text-zodiac/40 font-medium">Annual Operational Volume</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-accent"><Hospital className="w-5 h-5" /><span className="text-[10px] font-black uppercase tracking-widest">Operations</span></div>
                  <p className="text-2xl font-bold text-zodiac">Multi-Facility</p>
                  <p className="text-sm text-zodiac/40 font-medium">Distributed Supply Chain</p>
                </div>
              </div>
              <div className="space-y-10 pt-10 border-t border-zodiac/5">
                <h4 className="text-xs font-black uppercase tracking-[0.4em] text-zodiac/30">Strategic Expansion Roadmap</h4>
                <div className="space-y-8">
                  {[{ t: "Internal Ops Pilot", d: "Unified inventory & compliance workflows across 12 core sites." }, { t: "Distributed Hospital Pilots", d: "Scaling to 3–5 hospital pilots via authorized distributor network." }, { t: "National Rollout", d: "Distributor-led nationwide expansion across the medical hospital ecosystem." }, { t: "Regional Leadership", d: "Japan-based success blueprint exported to Southeast Asia (SEA)." }].map((milestone, i) => (
                    <div key={i} className="flex gap-6 group">
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full border border-zodiac/10 flex items-center justify-center font-black text-[10px] text-zodiac group-hover:bg-accent group-hover:text-white group-hover:border-accent transition-all duration-300">{i+1}</div>
                        {i < 3 && <div className="absolute top-10 left-1/2 -translate-x-1/2 w-px h-8 bg-zodiac/5"></div>}
                      </div>
                      <div className="pt-1"><h5 className="font-bold text-zodiac mb-1 tracking-tight">{milestone.t}</h5><p className="text-sm text-zodiac/50 font-light leading-relaxed">{milestone.d}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-12 lg:sticky lg:top-40">
              <div className="aspect-[4/5] bg-brandBg rounded-[4rem] overflow-hidden shadow-2xl relative group">
                <img src="https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&q=80&w=1200" alt="Medical Facility" className="w-full h-full object-cover grayscale opacity-50 group-hover:scale-110 transition-transform duration-[10s]" />
                <div className="absolute inset-0 bg-gradient-to-t from-zodiac via-zodiac/20 to-transparent"></div>
                <div className="absolute bottom-12 left-12 right-12 text-white">
                   <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
                      <span className="text-xs font-black uppercase tracking-[0.4em]">Validation Outcome</span>
                   </div>
                   <p className="text-xl md:text-2xl font-bold mb-4 tracking-tight leading-snug">"94% Accuracy in predictive stock alerts, reducing emergency expediting costs by 22%."</p>
                </div>
              </div>
              <div className="bg-zodiac p-12 rounded-[3rem] text-white">
                 <h4 className="text-xs font-black uppercase tracking-[0.4em] text-accent mb-8">Our Goal: Distributed Excellence</h4>
                 <p className="text-white/60 font-light leading-relaxed">Transitioning from reactive inventory monitoring to an <span className="text-white font-bold">Autonomous Execution Overlay</span> that manages fulfillment risk without human intervention.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Fit Section - MOVED ABOVE METHODOLOGY */}
      <section className="py-40 px-6 md:px-12 bg-brandBg border-t border-zodiac/5">
        <div className="max-w-7xl mx-auto">
          <StrategicFitSection />
          <ROICalculator />
        </div>
      </section>
      
      {/* Methodology Section */}
      <section className="py-40 px-6 md:px-12 bg-brandBg border-t border-zodiac/5">
        <div className="max-w-7xl mx-auto">
          <div className="mb-24 text-center lg:text-left">
            <p className="text-xs font-black uppercase tracking-[0.4em] text-accent mb-6">METHODOLOGY</p>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-zodiac mb-8">The 12-Week Pilot Roadmap.</h2>
            <p className="text-xl text-zodiac/50 font-light max-w-2xl leading-relaxed">A motion-driven framework designed to provide tangible operational lift within one quarter.</p>
          </div>
          <PilotRoadmap />
        </div>
      </section>

      {/* ENRICHED APPLICATION SECTION WITH PILOT READINESS CHECKLIST */}
      <section className="py-40 px-6 md:px-12 bg-white border-t border-zodiac/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-24 items-start">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.4em] text-accent mb-8">APPLICATION</p>
              <h2 className="text-4xl md:text-7xl font-bold tracking-tighter text-zodiac mb-12 leading-[0.95]">Pilot Readiness Checklist</h2>
              
              <div className="bg-brandBg/50 p-10 rounded-[3rem] border border-zodiac/5">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center shadow-lg shadow-accent/20">
                    <ClipboardCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-zodiac tracking-tight uppercase">Engagement Prerequisites</h4>
                    <p className="text-[10px] font-black uppercase tracking-widest text-zodiac/30">Strategic Alignment Audit</p>
                  </div>
                </div>

                <div className="space-y-10">
                  <div className="flex gap-6 group">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-all duration-300">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-bold text-zodiac mb-1 text-base tracking-tight">Timing materially affects outcomes</h5>
                      <p className="text-sm text-zodiac/50 font-light leading-relaxed">Delays in decision-making create measurable cost, risk, or service impact once commitments are made.</p>
                    </div>
                  </div>

                  <div className="flex gap-6 group">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-all duration-300">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-bold text-zodiac mb-1 text-base tracking-tight">Sufficient operational scale</h5>
                      <p className="text-sm text-zodiac/50 font-light leading-relaxed">You manage €10M+ in annual operational volume, with complexity across teams, sites, or partners.</p>
                    </div>
                  </div>

                  <div className="flex gap-6 group">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-all duration-300">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-bold text-zodiac mb-1 text-base tracking-tight">Clear operational ownership</h5>
                      <p className="text-sm text-zodiac/50 font-light leading-relaxed">A single operational owner is accountable for decisions and outcomes throughout the 12-week pilot.</p>
                    </div>
                  </div>

                  <div className="flex gap-6 group">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-all duration-300">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-bold text-zodiac mb-1 text-base tracking-tight">Authority to approve innovation spend</h5>
                      <p className="text-sm text-zodiac/50 font-light leading-relaxed">The pilot sponsor has the ability to approve innovation or transformation budgets if the pilot proves value.</p>
                    </div>
                  </div>

                  <div className="flex gap-6 group">
                    <div className="shrink-0 w-8 h-8 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center group-hover:bg-accent group-hover:text-white transition-all duration-300">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                    <div>
                      <h5 className="font-bold text-zodiac mb-1 text-base tracking-tight">Defined success metrics</h5>
                      <p className="text-sm text-zodiac/50 font-light leading-relaxed">You have clear KPIs tied to decision speed, risk reduction, service levels, cost avoidance, or operational confidence and not just activity or reporting outputs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-brandBg p-12 lg:p-16 rounded-[4rem] border border-zodiac/5 shadow-2xl lg:sticky lg:top-40">
              <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-3"><label className="text-[10px] font-black uppercase tracking-widest text-zodiac/40">Full Name</label><input type="text" placeholder="Jane Cooper" className="w-full bg-white border border-zodiac/10 rounded-xl px-6 py-4 focus:outline-none focus:border-accent transition-colors shadow-sm" /></div>
                  <div className="space-y-3"><label className="text-[10px] font-black uppercase tracking-widest text-zodiac/40">Company Email</label><input type="email" placeholder="jane@enterprise.com" className="w-full bg-white border border-zodiac/10 rounded-xl px-6 py-4 focus:outline-none focus:border-accent transition-colors shadow-sm" /></div>
                </div>
                <div className="space-y-3"><label className="text-[10px] font-black uppercase tracking-widest text-zodiac/40">Company Name</label><input type="text" placeholder="Acme Global Logistics" className="w-full bg-white border border-zodiac/10 rounded-xl px-6 py-4 focus:outline-none focus:border-accent transition-colors shadow-sm" /></div>
                <div className="space-y-3"><label className="text-[10px] font-black uppercase tracking-widest text-zodiac/40">Operational Challenge (Primary Decision Moment)</label><textarea rows={4} placeholder="Describe the decision loop that currently breaks under pressure..." className="w-full bg-white border border-zodiac/10 rounded-xl px-6 py-4 focus:outline-none focus:border-accent transition-colors shadow-sm resize-none"></textarea></div>
                <div className="pt-6">
                  <button className="w-full bg-zodiac text-white py-6 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-accent transition-executive shadow-xl shadow-zodiac/10 flex items-center justify-center gap-4">SUBMIT PILOT APPLICATION<ArrowRight className="w-4 h-4" /></button>
                  <p className="text-[9px] text-zodiac/30 font-bold uppercase tracking-widest text-center mt-6">By submitting, you agree to our Enterprise Privacy & Governance Terms.</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-60 px-6 md:px-12 bg-brandBg relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-5xl md:text-8xl font-bold tracking-tighter text-zodiac mb-10 leading-[1] transition-all duration-700">Scale Execution. <span className="text-accent">With Confidence.</span></h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
            <button onClick={() => window.open(BOOKING_URL, '_blank')} className="w-full sm:w-auto bg-zodiac text-white px-12 py-7 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl hover:bg-accent transition-executive flex items-center justify-center gap-4 group"><PhoneCall className="w-4 h-4" />Book A Strategic Call<ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" /></button>
            <button onClick={() => window.open(BOOKING_URL, '_blank')} className="text-zodiac font-black text-xs uppercase tracking-[0.3em] hover:text-accent transition-executive border-b-2 border-zodiac/10 hover:border-accent pb-2">Request Pilot Pricing</button>
          </div>
        </div>
      </section>
    </div>
  );
};
